import React, { useState } from "react";
import {
    createCalendar,
    getWeeksInMonth,
    isToday,
} from "@internationalized/date";
import {
    useCalendar,
    useCalendarCell,
    useCalendarGrid,
} from "@react-aria/calendar";

import { DateButton } from "shared/components/date-controls/date-picker/DateButton.styles";
import DatePickerDisplayMonthAndYear from "shared/components/date-controls/date-picker/DatePickerDisplayMonthAndYear";
import HStack from "shared/components/layout/stack/HStack";
import PropTypes from "prop-types";
import { StyledTertiaryButton } from "./DatePickerCalendar.styles";
import TertiaryButton from "shared/components/inputs/button/TertiaryButton";
import Text from "shared/components/data-display/text/Text";
import { useCalendarState } from "@react-stately/calendar";
import { useLocale } from "@react-aria/i18n";

export const CalendarTitleBlockPadding = "19px 16px";

function CalendarCell({ state, date }) {
    const ref = React.useRef();
    const {
        cellProps,
        buttonProps,
        isSelected,
        isOutsideVisibleRange,
        isDisabled,
        isUnavailable,
        formattedDate,
    } = useCalendarCell({ date }, state, ref);

    return (
        <td {...cellProps}>
            {isDisabled || isUnavailable ? (
                <DateButton
                    ref={ref}
                    hidden={isOutsideVisibleRange}
                    isSelected={isSelected}
                    isUnavailable={isUnavailable}
                    disabled={isDisabled}
                    isCurrent={isToday(date)}
                >
                    {formattedDate}
                </DateButton>
            ) : (
                <DateButton
                    {...buttonProps}
                    ref={ref}
                    hidden={isOutsideVisibleRange}
                    isSelected={isSelected}
                    isUnavailable={isUnavailable}
                    disabled={isDisabled}
                    isCurrent={isToday(date)}
                >
                    {formattedDate}
                </DateButton>
            )}
        </td>
    );
}

CalendarCell.propTypes = {
    state: PropTypes.object,
    date: PropTypes.object,
};

function CalendarGrid({ state, ...props }) {
    const { locale } = useLocale();
    const { gridProps, headerProps, weekDays } = useCalendarGrid(props, state);

    const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

    return (
        <table
            {...gridProps}
            style={{ padding: "0 7px 0 16px", margin: "0 auto" }}
        >
            <thead {...headerProps}>
                <tr>
                    {weekDays.map((day, index) => (
                        <th
                            key={`${day}-${index}`}
                            style={{
                                width: "33px",
                                paddingBottom: "7px",
                                paddingRight: "9px",
                                textAlign: "center",
                            }}
                        >
                            <Text variant="Descriptive_1_1" color="Gray_3">
                                {day}
                            </Text>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
                    <tr key={weekIndex}>
                        {state
                            .getDatesInWeek(weekIndex)
                            .map((date, i) =>
                                date ? (
                                    <CalendarCell
                                        key={`${date.year}-${date.month}-${date.day}`}
                                        state={state}
                                        date={date}
                                    />
                                ) : (
                                    <td key={`empty-${weekIndex}-${i}`} />
                                )
                            )}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

CalendarGrid.propTypes = {
    state: PropTypes.object,
};

function DatePickerCalendar({
    onClickCancel,
    onClickDone,
    ...props
}) {
    const { locale } = useLocale();
    const state = useCalendarState({
        ...props,
        locale,
        createCalendar,
    });
    const ref = React.useRef();
    const { calendarProps, prevButtonProps, nextButtonProps, title } =
        useCalendar(props, state, ref);
    const [showMonthAndYear, setShowMonthAndYear] = useState(false);

    return (
        <div {...calendarProps} ref={ref}>
            <HStack
                align="center"
                justify="space-between"
                style={{ padding: CalendarTitleBlockPadding }}
            >
                <TertiaryButton
                    trailingIcon={showMonthAndYear ? "caret-down" : "caret-up"}
                    label={title}
                    onClick={() => setShowMonthAndYear(!showMonthAndYear)}
                />

                <HStack>
                    <StyledTertiaryButton
                        trailingIcon="caret-left"
                        onClick={prevButtonProps.onPress}
                        {...Object.entries(prevButtonProps).splice(
                            Object.keys(prevButtonProps).indexOf("onPress"),
                            1
                        )}
                        {...{ "aria-label": "Prev" }}
                    />

                    <StyledTertiaryButton
                        trailingIcon="caret-right"
                        onClick={nextButtonProps.onPress}
                        {...Object.entries(nextButtonProps).splice(
                            Object.keys(nextButtonProps).indexOf("onPress"),
                            1
                        )}
                        {...{ "aria-label": "Next" }}
                    />
                </HStack>
            </HStack>
            {showMonthAndYear ? (
                <DatePickerDisplayMonthAndYear
                    state={state}
                    setShowMonthAndYear={setShowMonthAndYear}
                />
            ) : (
                <>
                    <CalendarGrid state={state} />
                    <HStack
                        justify="space-between"
                        style={{ padding: "5px 25px 14px 25px" }}
                    >
                        <TertiaryButton
                            label="Cancel"
                            onClick={onClickCancel}
                        />
                        <TertiaryButton label="Done" onClick={onClickDone} />
                    </HStack>
                </>
            )}
        </div>
    );
}

DatePickerCalendar.propTypes = {
    onClickCancel: PropTypes.func,
    onClickDone: PropTypes.func,
};

export default DatePickerCalendar;
